import { MenuItem } from '@mui/material';
import WarningIcon from '@mui/icons-material/Warning';
import { Post } from 'lib/types';
import { useApi } from 'lib/contexts/ApplicationState';
import { DropdownMenu } from 'components/DropdownMenu';
import { useSnackbar } from 'notistack';
import { useModal } from 'mui-modal-provider';
import { useState } from 'react';
import { PostOwnerModal } from './PostOwnerModal';
import { PostAnalysisModal } from './PostAnalysisModal';
import { UpdatePostInfoModal } from './UpdatePostInfoModal';

interface Props {
  post: Post;
  onOpenUpdateMetrics: () => void;
  onChange: () => void;
}

export const AdminPostMenu = ({
  post, onOpenUpdateMetrics, onChange,
}: Props) => {
  const API = useApi();
  const { enqueueSnackbar } = useSnackbar();
  const { showModal } = useModal();
  const [isAnalyzing, setIsAnalyzing] = useState(false);

  const copyEmbed = () => {
    const text = `<iframe
        title="Credits via Gondola"
        src="${window.location.href}/credits"
      />`;

    if (navigator.clipboard) {
      navigator.clipboard.writeText(text);
      enqueueSnackbar('Embed code copied!', { variant: 'success' });
    }
  };

  const reindexPost = async () => {
    try {
      await API.reindexPost(post.id);
      enqueueSnackbar('Post reindexing complete!', { variant: 'success' });
    } catch (err) {
      enqueueSnackbar(`Error reindexing post: ${err}`, { variant: 'error' });
    }
  };

  const updatePostOwner = async () => {
    const modal = showModal(PostOwnerModal, {
      onClose: () => {
        modal.destroy();
        onChange();
      },
      postOwner: post.networkAccount,
      postId: post.id,
      network: post.network,
    });
  };

  const updatePostInfo = async () => {
    const modal = showModal(UpdatePostInfoModal, {
      onClose: () => {
        modal.destroy();
        onChange();
      },
      post,
    });
  };

  const analyzePost = async () => {
    if (isAnalyzing) return;

    setIsAnalyzing(true);
    try {
      const analysis = await API.analyzePost(post.id);
      const modal = showModal(PostAnalysisModal, {
        onClose: () => {
          modal.destroy();
        },
        analysis,
        postId: post.id,
      });
    } catch (err) {
      enqueueSnackbar(`Error analyzing post: ${err}`, { variant: 'error' });
    } finally {
      setIsAnalyzing(false);
    }
  };

  return (
    <DropdownMenu altIcon>
      <>
        <MenuItem onClick={onOpenUpdateMetrics}>
          Update metrics
        </MenuItem>
        <MenuItem onClick={copyEmbed}>
          Copy embed code
        </MenuItem>
        <MenuItem onClick={reindexPost}>
          Reindex post
        </MenuItem>
        <MenuItem onClick={updatePostOwner}>
          Post owner:
          {' '}
          {post.networkAccount?.postingAccountOwner?.user?.username || 'Not set'}
        </MenuItem>
        <MenuItem onClick={updatePostInfo}>
          {post.provisional && <WarningIcon color="warning" />}
          Update post details
        </MenuItem>
        <MenuItem onClick={analyzePost} disabled={isAnalyzing}>
          {isAnalyzing ? 'Analyzing...' : 'Analyze Post'}
        </MenuItem>
      </>
    </DropdownMenu>
  );
};
